import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import { PsyToolsId } from '../../components/meet/tools-panel/types';
import { Line } from '../../components/tools/clouds/types';
import { Figure, PlacedFigure } from '../../components/tools/sand-therapy/types';
import { useAppSelector } from '../../hooks/redux';
import { IJournalResult } from '../../store/types';

type StateSetterValue<T> = T | ((prev: T) => T);
type ParticipantDisplayNames = Record<string, string>;

interface MeetToolsState {
  toolsMenu: PsyToolsId | 0;
  hasColorCodeActiveTab: boolean;
  cloudsLines: Line[];
  cloudsSelectedImage: number | null;
  cloudsClientFinished: boolean;
  cloudsHideVideoForClient: boolean;
  macPanelExpanded: boolean;
  macClientPanelExpanded: boolean;
  emotionsPanelExpanded: boolean;
  emotionsClientPanelExpanded: boolean;
  sandPlacedFigures: PlacedFigure[];
  sandSelectedFigures: Figure[];
  sandColor: string;
  sandClientReady: boolean;
  macGroupDeckShown: boolean;
  macGroupExpectedParticipantIds: string[];
  macGroupParticipantDisplayNames: ParticipantDisplayNames;
  emotionsGroupResultsShown: boolean;
  emotionsGroupExpectedParticipantIds: string[];
  emotionsGroupParticipantDisplayNames: ParticipantDisplayNames;
}

type MeetToolsAction =
  | { type: 'setToolsMenu'; payload: PsyToolsId | 0 }
  | { type: 'setHasColorCodeActiveTab'; payload: boolean }
  | { type: 'setCloudsLines'; payload: StateSetterValue<Line[]> }
  | { type: 'setCloudsSelectedImage'; payload: number | null }
  | { type: 'setCloudsClientFinished'; payload: boolean }
  | { type: 'setCloudsHideVideoForClient'; payload: boolean }
  | { type: 'setMacPanelExpanded'; payload: boolean }
  | { type: 'setMacClientPanelExpanded'; payload: boolean }
  | { type: 'setEmotionsPanelExpanded'; payload: boolean }
  | { type: 'setEmotionsClientPanelExpanded'; payload: boolean }
  | { type: 'setSandPlacedFigures'; payload: PlacedFigure[] }
  | { type: 'setSandSelectedFigures'; payload: Figure[] }
  | { type: 'setSandColor'; payload: string }
  | { type: 'setSandClientReady'; payload: boolean }
  | { type: 'setMacGroupDeckShown'; payload: boolean }
  | { type: 'setMacGroupExpectedParticipantIds'; payload: string[] }
  | { type: 'setMacGroupParticipantDisplayNames'; payload: ParticipantDisplayNames }
  | { type: 'setEmotionsGroupResultsShown'; payload: boolean }
  | { type: 'setEmotionsGroupExpectedParticipantIds'; payload: string[] }
  | { type: 'setEmotionsGroupParticipantDisplayNames'; payload: ParticipantDisplayNames };

interface MeetToolsContextValue extends MeetToolsState {
  activeTool: PsyToolsId | 0;
  result: IJournalResult;
  setResult: (value: IJournalResult) => void;
  handleToolActivated: (toolId: string, properties?: Record<string, unknown>) => void;
  setToolsMenu: (value: PsyToolsId | 0) => void;
  setHasColorCodeActiveTab: (value: boolean) => void;
  setCloudsLines: (value: StateSetterValue<Line[]>) => void;
  setCloudsSelectedImage: (value: StateSetterValue<number | null>) => void;
  setCloudsClientFinished: (value: StateSetterValue<boolean>) => void;
  setCloudsHideVideoForClient: (value: StateSetterValue<boolean>) => void;
  setMacPanelExpanded: (value: StateSetterValue<boolean>) => void;
  setMacClientPanelExpanded: (value: StateSetterValue<boolean>) => void;
  setEmotionsPanelExpanded: (value: StateSetterValue<boolean>) => void;
  setEmotionsClientPanelExpanded: (value: StateSetterValue<boolean>) => void;
  setSandPlacedFigures: (value: StateSetterValue<PlacedFigure[]>) => void;
  setSandSelectedFigures: (value: StateSetterValue<Figure[]>) => void;
  setSandColor: (value: StateSetterValue<string>) => void;
  setSandClientReady: (value: StateSetterValue<boolean>) => void;
  setMacGroupDeckShown: (value: StateSetterValue<boolean>) => void;
  setMacGroupExpectedParticipantIds: (value: StateSetterValue<string[]>) => void;
  setMacGroupParticipantDisplayNames: (value: StateSetterValue<ParticipantDisplayNames>) => void;
  setEmotionsGroupResultsShown: (value: StateSetterValue<boolean>) => void;
  setEmotionsGroupExpectedParticipantIds: (value: StateSetterValue<string[]>) => void;
  setEmotionsGroupParticipantDisplayNames: (value: StateSetterValue<ParticipantDisplayNames>) => void;
}

interface MeetToolsProviderProps {
  result: IJournalResult;
  setResult: (value: IJournalResult) => void;
  handleToolActivated: (toolId: string, properties?: Record<string, unknown>) => void;
  onApi?: (api: MeetToolsBridgeApi) => void;
}

export interface MeetToolsBridgeApi {
  setToolsMenu: (value: PsyToolsId | 0) => void;
  setCloudsLines: (value: StateSetterValue<Line[]>) => void;
  setCloudsSelectedImage: (value: StateSetterValue<number | null>) => void;
  setCloudsClientFinished: (value: StateSetterValue<boolean>) => void;
  setSandPlacedFigures: (value: StateSetterValue<PlacedFigure[]>) => void;
  setSandSelectedFigures: (value: StateSetterValue<Figure[]>) => void;
  setSandColor: (value: StateSetterValue<string>) => void;
  setSandClientReady: (value: StateSetterValue<boolean>) => void;
  setMacGroupDeckShown: (value: StateSetterValue<boolean>) => void;
  setEmotionsGroupResultsShown: (value: StateSetterValue<boolean>) => void;
}

const initialState: MeetToolsState = {
  toolsMenu: 0,
  hasColorCodeActiveTab: false,
  cloudsLines: [],
  cloudsSelectedImage: null,
  cloudsClientFinished: false,
  cloudsHideVideoForClient: false,
  macPanelExpanded: false,
  macClientPanelExpanded: false,
  emotionsPanelExpanded: false,
  emotionsClientPanelExpanded: false,
  sandPlacedFigures: [],
  sandSelectedFigures: [],
  sandColor: '#fef3c7',
  sandClientReady: false,
  macGroupDeckShown: false,
  macGroupExpectedParticipantIds: [],
  macGroupParticipantDisplayNames: {},
  emotionsGroupResultsShown: false,
  emotionsGroupExpectedParticipantIds: [],
  emotionsGroupParticipantDisplayNames: {},
};

const MeetToolsContext = createContext<MeetToolsContextValue | null>(null);

function meetToolsReducer(state: MeetToolsState, action: MeetToolsAction): MeetToolsState {
  switch (action.type) {
    case 'setToolsMenu':
      return { ...state, toolsMenu: action.payload };
    case 'setHasColorCodeActiveTab':
      return { ...state, hasColorCodeActiveTab: action.payload };
    case 'setCloudsLines':
      return { ...state, cloudsLines: resolveNextState(action.payload, state.cloudsLines) };
    case 'setCloudsSelectedImage':
      return { ...state, cloudsSelectedImage: action.payload };
    case 'setCloudsClientFinished':
      return { ...state, cloudsClientFinished: action.payload };
    case 'setCloudsHideVideoForClient':
      return { ...state, cloudsHideVideoForClient: action.payload };
    case 'setMacPanelExpanded':
      return { ...state, macPanelExpanded: action.payload };
    case 'setMacClientPanelExpanded':
      return { ...state, macClientPanelExpanded: action.payload };
    case 'setEmotionsPanelExpanded':
      return { ...state, emotionsPanelExpanded: action.payload };
    case 'setEmotionsClientPanelExpanded':
      return { ...state, emotionsClientPanelExpanded: action.payload };
    case 'setSandPlacedFigures':
      return { ...state, sandPlacedFigures: action.payload };
    case 'setSandSelectedFigures':
      return { ...state, sandSelectedFigures: action.payload };
    case 'setSandColor':
      return { ...state, sandColor: action.payload };
    case 'setSandClientReady':
      return { ...state, sandClientReady: action.payload };
    case 'setMacGroupDeckShown':
      return { ...state, macGroupDeckShown: action.payload };
    case 'setMacGroupExpectedParticipantIds':
      return { ...state, macGroupExpectedParticipantIds: action.payload };
    case 'setMacGroupParticipantDisplayNames':
      return { ...state, macGroupParticipantDisplayNames: action.payload };
    case 'setEmotionsGroupResultsShown':
      return { ...state, emotionsGroupResultsShown: action.payload };
    case 'setEmotionsGroupExpectedParticipantIds':
      return { ...state, emotionsGroupExpectedParticipantIds: action.payload };
    case 'setEmotionsGroupParticipantDisplayNames':
      return { ...state, emotionsGroupParticipantDisplayNames: action.payload };
    default:
      return state;
  }
}

function resolveNextState<T>(value: StateSetterValue<T>, current: T): T {
  return typeof value === 'function' ? (value as (prev: T) => T)(current) : value;
}

export function MeetToolsProvider({
  children,
  result,
  setResult,
  handleToolActivated,
  onApi,
}: PropsWithChildren<MeetToolsProviderProps>) {
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);
  const [state, dispatch] = useReducer(meetToolsReducer, initialState);

  const setToolsMenu = useCallback((value: PsyToolsId | 0) => {
    dispatch({ type: 'setToolsMenu', payload: value });
  }, []);

  const setHasColorCodeActiveTab = useCallback((value: boolean) => {
    dispatch({ type: 'setHasColorCodeActiveTab', payload: value });
  }, []);

  const setCloudsLines = useCallback(
    (value: StateSetterValue<Line[]>) => {
      dispatch({ type: 'setCloudsLines', payload: value });
    },
    [],
  );

  const setCloudsSelectedImage = useCallback(
    (value: StateSetterValue<number | null>) => {
      dispatch({ type: 'setCloudsSelectedImage', payload: resolveNextState(value, state.cloudsSelectedImage) });
    },
    [state.cloudsSelectedImage],
  );

  const setCloudsClientFinished = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({ type: 'setCloudsClientFinished', payload: resolveNextState(value, state.cloudsClientFinished) });
    },
    [state.cloudsClientFinished],
  );

  const setCloudsHideVideoForClient = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({
        type: 'setCloudsHideVideoForClient',
        payload: resolveNextState(value, state.cloudsHideVideoForClient),
      });
    },
    [state.cloudsHideVideoForClient],
  );

  const setMacPanelExpanded = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({ type: 'setMacPanelExpanded', payload: resolveNextState(value, state.macPanelExpanded) });
    },
    [state.macPanelExpanded],
  );

  const setMacClientPanelExpanded = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({
        type: 'setMacClientPanelExpanded',
        payload: resolveNextState(value, state.macClientPanelExpanded),
      });
    },
    [state.macClientPanelExpanded],
  );

  const setEmotionsPanelExpanded = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({
        type: 'setEmotionsPanelExpanded',
        payload: resolveNextState(value, state.emotionsPanelExpanded),
      });
    },
    [state.emotionsPanelExpanded],
  );

  const setEmotionsClientPanelExpanded = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({
        type: 'setEmotionsClientPanelExpanded',
        payload: resolveNextState(value, state.emotionsClientPanelExpanded),
      });
    },
    [state.emotionsClientPanelExpanded],
  );

  const setSandPlacedFigures = useCallback(
    (value: StateSetterValue<PlacedFigure[]>) => {
      dispatch({ type: 'setSandPlacedFigures', payload: resolveNextState(value, state.sandPlacedFigures) });
    },
    [state.sandPlacedFigures],
  );

  const setSandSelectedFigures = useCallback(
    (value: StateSetterValue<Figure[]>) => {
      dispatch({ type: 'setSandSelectedFigures', payload: resolveNextState(value, state.sandSelectedFigures) });
    },
    [state.sandSelectedFigures],
  );

  const setSandColor = useCallback(
    (value: StateSetterValue<string>) => {
      dispatch({ type: 'setSandColor', payload: resolveNextState(value, state.sandColor) });
    },
    [state.sandColor],
  );

  const setSandClientReady = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({ type: 'setSandClientReady', payload: resolveNextState(value, state.sandClientReady) });
    },
    [state.sandClientReady],
  );

  const setMacGroupDeckShown = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({
        type: 'setMacGroupDeckShown',
        payload: resolveNextState(value, state.macGroupDeckShown),
      });
    },
    [state.macGroupDeckShown],
  );

  const setMacGroupExpectedParticipantIds = useCallback(
    (value: StateSetterValue<string[]>) => {
      dispatch({
        type: 'setMacGroupExpectedParticipantIds',
        payload: resolveNextState(value, state.macGroupExpectedParticipantIds),
      });
    },
    [state.macGroupExpectedParticipantIds],
  );

  const setMacGroupParticipantDisplayNames = useCallback(
    (value: StateSetterValue<ParticipantDisplayNames>) => {
      dispatch({
        type: 'setMacGroupParticipantDisplayNames',
        payload: resolveNextState(value, state.macGroupParticipantDisplayNames),
      });
    },
    [state.macGroupParticipantDisplayNames],
  );

  const setEmotionsGroupResultsShown = useCallback(
    (value: StateSetterValue<boolean>) => {
      dispatch({
        type: 'setEmotionsGroupResultsShown',
        payload: resolveNextState(value, state.emotionsGroupResultsShown),
      });
    },
    [state.emotionsGroupResultsShown],
  );

  const setEmotionsGroupExpectedParticipantIds = useCallback(
    (value: StateSetterValue<string[]>) => {
      dispatch({
        type: 'setEmotionsGroupExpectedParticipantIds',
        payload: resolveNextState(value, state.emotionsGroupExpectedParticipantIds),
      });
    },
    [state.emotionsGroupExpectedParticipantIds],
  );

  const setEmotionsGroupParticipantDisplayNames = useCallback(
    (value: StateSetterValue<ParticipantDisplayNames>) => {
      dispatch({
        type: 'setEmotionsGroupParticipantDisplayNames',
        payload: resolveNextState(value, state.emotionsGroupParticipantDisplayNames),
      });
    },
    [state.emotionsGroupParticipantDisplayNames],
  );

  const value = useMemo<MeetToolsContextValue>(
    () => ({
      activeTool,
      result,
      setResult,
      handleToolActivated,
      ...state,
      setToolsMenu,
      setHasColorCodeActiveTab,
      setCloudsLines,
      setCloudsSelectedImage,
      setCloudsClientFinished,
      setCloudsHideVideoForClient,
      setMacPanelExpanded,
      setMacClientPanelExpanded,
      setEmotionsPanelExpanded,
      setEmotionsClientPanelExpanded,
      setSandPlacedFigures,
      setSandSelectedFigures,
      setSandColor,
      setSandClientReady,
      setMacGroupDeckShown,
      setMacGroupExpectedParticipantIds,
      setMacGroupParticipantDisplayNames,
      setEmotionsGroupResultsShown,
      setEmotionsGroupExpectedParticipantIds,
      setEmotionsGroupParticipantDisplayNames,
    }),
    [
      activeTool,
      handleToolActivated,
      result,
      setCloudsClientFinished,
      setCloudsHideVideoForClient,
      setCloudsLines,
      setCloudsSelectedImage,
      setEmotionsClientPanelExpanded,
      setEmotionsGroupExpectedParticipantIds,
      setEmotionsGroupParticipantDisplayNames,
      setEmotionsGroupResultsShown,
      setEmotionsPanelExpanded,
      setHasColorCodeActiveTab,
      setMacGroupDeckShown,
      setMacGroupExpectedParticipantIds,
      setMacGroupParticipantDisplayNames,
      setMacClientPanelExpanded,
      setMacPanelExpanded,
      setResult,
      setToolsMenu,
      setSandPlacedFigures,
      setSandSelectedFigures,
      setSandColor,
      setSandClientReady,
      state,
    ],
  );

  useEffect(() => {
    onApi?.({
      setToolsMenu,
      setCloudsLines,
      setCloudsSelectedImage,
      setCloudsClientFinished,
      setSandPlacedFigures,
      setSandSelectedFigures,
      setSandColor,
      setSandClientReady,
      setMacGroupDeckShown,
      setEmotionsGroupResultsShown,
    });
  }, [
    onApi,
    setCloudsClientFinished,
    setCloudsLines,
    setCloudsSelectedImage,
    setToolsMenu,
    setSandPlacedFigures,
    setSandSelectedFigures,
    setSandColor,
    setSandClientReady,
    setEmotionsGroupResultsShown,
    setMacGroupDeckShown,
  ]);

  return <MeetToolsContext.Provider value={value}>{children}</MeetToolsContext.Provider>;
}

export function useMeetTools() {
  const context = useContext(MeetToolsContext);

  if (!context) {
    throw new Error('useMeetTools must be used within MeetToolsProvider');
  }

  return context;
}
